/**
 * One write, and the three things a form has to say about it.
 *
 * The two forms in WP-K2 — an attendance correction and a manual time entry — are
 * the only places on an employee's screens where something is sent rather than
 * read, and both are governed by a policy the client cannot read (see
 * `web/shapes/my-activity.md` §8). So the interesting state here is not "did it
 * work" but **the refusal**: it carries `earliest_allowed_at`, or the id of the
 * entry in the way, and that body is the only place the employee learns the rule.
 * `error` is therefore the full `WorkforceApiError`, `details` included, and never
 * flattened to a string.
 *
 * `submit` resolves rather than rejects. A form's submit handler cannot be `async`
 * without tripping `@typescript-eslint/no-misused-promises`, so it calls
 * `void submit(...)`, and a promise that rejected there would be an unhandled
 * rejection in the browser console with the failure already rendered on screen.
 */

import { useCallback, useState } from 'react';

import { WorkforceApiError } from './errors';
import { useSession } from '../auth/SessionProvider';

export type MutationStatus = 'idle' | 'pending' | 'ok' | 'error';

export interface MutationResult<TBody, TResult> {
  readonly status: MutationStatus;
  readonly data: TResult | null;
  readonly error: WorkforceApiError | null;
  /**
   * `pathOverride` is for a write whose target is chosen **per press** rather than
   * per mount — WP-K5 revokes a registration token by id from a table row, and the
   * row is not known when the hook is created. Additive and optional: every existing
   * caller passes one argument and binds its path at the hook, which stays the
   * normal case, because a path decided at the call site is a path a reader has to
   * hunt for.
   */
  readonly submit: (
    body: TBody,
    pathOverride?: string,
    headerOverride?: Readonly<Record<string, string>>,
  ) => Promise<void>;
  /**
   * The `ETag` the **write's own response** carried, unquoted, or `null`.
   *
   * Every incident write answers with a fresh one, so a client that patches twice
   * in a row does not have to re-read to get its next precondition (WP-L5).
   */
  readonly etag: string | null;
  /** Clears the last outcome — what "close the form and start again" needs. */
  readonly reset: () => void;
}

export function useApiMutation<TBody, TResult>(
  path: string,
  /**
   * `DELETE` joined `POST`/`PATCH` for Task T2-D's revoke call, and `PUT` joined
   * them for Task T2-C-web's `PUT /geo/policy` upsert — `useApiMutation` itself is
   * unopinionated about the verb (`client.requestWithHeaders` already accepts any
   * method string and attaches the CSRF header to every unsafe one); the union
   * here exists only so a caller cannot typo a verb this hook has never sent.
   * `submit`'s `body` is typed `TBody`, so a caller with nothing to send instantiates
   * the hook at `TBody = undefined` and calls `submit(undefined, ...)` — `DELETE
   * /admin/permission-grants/{id}` takes no request body at all.
   */
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST',
  /**
   * Headers every submit from this hook carries — `X-Access-Reason` in practice,
   * which is mandatory in v1 on every sensitive-access operation and does not vary
   * per press. `headerOverride` on `submit` is merged over these, and is where a
   * per-press `If-Match` goes: the precondition changes with every read, so binding
   * it at the hook would send a stale one the first time the record moved.
   */
  headers: Readonly<Record<string, string>> = {},
): MutationResult<TBody, TResult> {
  const { client } = useSession();
  const [status, setStatus] = useState<MutationStatus>('idle');
  const [data, setData] = useState<TResult | null>(null);
  const [etag, setEtag] = useState<string | null>(null);
  const [error, setError] = useState<WorkforceApiError | null>(null);

  // Same reason `useApiQuery` serialises: an inline object at the call site is a new
  // identity every render, and `submit` would be a new function on every one of them.
  const headerKey = JSON.stringify(headers);

  const submit = useCallback(
    async (
      body: TBody,
      pathOverride?: string,
      headerOverride?: Readonly<Record<string, string>>,
    ): Promise<void> => {
      setStatus('pending');
      setError(null);
      try {
        const bound = JSON.parse(headerKey) as Record<string, string>;
        const response = await client.requestWithHeaders<TResult>(pathOverride ?? path, {
          method,
          body,
          headers: { ...bound, ...headerOverride },
        });
        setData(response.body);
        setEtag(response.etag);
        setStatus('ok');
      } catch (cause) {
        setData(null);
        setError(
          cause instanceof WorkforceApiError
            ? cause
            : new WorkforceApiError({
                code: 'CLIENT_TRANSPORT_FAILED',
                message: cause instanceof Error ? cause.message : 'request failed',
                status: 0,
              }),
        );
        setStatus('error');
      }
    },
    [client, path, method, headerKey],
  );

  const reset = useCallback(() => {
    setStatus('idle');
    setData(null);
    setEtag(null);
    setError(null);
  }, []);

  return { status, data, error, etag, submit, reset };
}

/**
 * `details.current_etag` from a `409 WORKFORCE_VERSION_CONFLICT`.
 *
 * Read so the refusal can **say** what the live token is, not so the client can
 * quietly retry with it: retrying under the conflict is precisely the blind
 * overwrite `If-Match` exists to prevent, and the operator has an unsaved edit on
 * screen that only they can decide about. API_CONTRACT §6 lists the field for
 * exactly one purpose — "để client refetch".
 */
export function currentEtag(error: WorkforceApiError): string | null {
  const value = error.details.current_etag;
  return typeof value === 'string' ? value : null;
}

/**
 * `details.allowed_transitions[]` from a `409 WORKFORCE_INVALID_STATE_TRANSITION`.
 *
 * The server's own answer to "what could I have done instead", printed rather than
 * re-derived from the client's table: if the two ever disagree, the one that decides
 * is the server's, and a screen that printed its own list would hide the
 * disagreement at the exact moment it mattered.
 */
export function allowedTransitionsFromRefusal(error: WorkforceApiError): readonly string[] {
  const value = error.details.allowed_transitions;
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === 'string');
}

/**
 * `details.earliest_allowed_at` from a `409 WORKFORCE_REASON_WINDOW_EXPIRED`.
 *
 * This value is the **only** published statement of
 * `wp_policies.correction_max_days_back` a browser can obtain: no session endpoint
 * returns the policy. Reading it out of the refusal and putting it on screen is how
 * the employee learns the boundary from the authority that owns it, instead of from
 * a client-side guess that would be wrong for every tenant that changed the default.
 */
export function earliestAllowedAt(error: WorkforceApiError): string | null {
  const value = error.details.earliest_allowed_at;
  return typeof value === 'string' ? value : null;
}

/** `details.conflicting_entry_id` / `entry_id` from a `409` overlap, when present. */
export function conflictingEntryId(error: WorkforceApiError): string | null {
  const detail = error.details;
  for (const key of ['conflicting_entry_id', 'entry_id', 'time_entry_id']) {
    const value = detail[key];
    if (typeof value === 'string') return value;
  }
  return null;
}
